import { InputValue, MinValue, MaxValue } from '../NumberInput.types';

const isEmptyValue = (value: InputValue): boolean =>
  value === null || value === undefined || value === '';

const isInvalidNumber = (value: InputValue): boolean =>
  isEmptyValue(value) || !/[0-9]$/.test(String(value));

export const defaultValueToNullIfEmpty = (
  value: InputValue,
  defaultValue: InputValue,
): string | number => {
  if (!isEmptyValue(value)) {
    return String(value);
  }

  return !isEmptyValue(defaultValue) ? Number(defaultValue) : '';
};

export const defaultValueToNullIfInvalidNumber = (
  value: string | number,
): null | number => (isInvalidNumber(value) ? null : Number(value));

const normalizeStringValue = (value: string): string => {
  const valueWithDotDecimalSeparator = value.replace(',', '.');
  if (isInvalidNumber(valueWithDotDecimalSeparator)) {
    return valueWithDotDecimalSeparator;
  }

  // Preserve minus sign when typing '-00'.
  return valueWithDotDecimalSeparator.startsWith('-')
    ? valueWithDotDecimalSeparator.replace(/^-0+(?!\.|,|$)/, '-')
    : valueWithDotDecimalSeparator.replace(/^0+(?!\.|,|$)/, '');
};

export const normalizeValues = (value: InputValue) => {
  const stringValue = normalizeStringValue(String(value));
  const numberValue = stringValue === '' ? null : Number(stringValue);

  return {
    numberValue,
    stringValue,
  };
};

export const isInRange = ({
  value,
  minValue,
  maxValue,
}: {
  value: number;
  minValue: MinValue;
  maxValue: MaxValue;
}): boolean =>
  !(!Number.isNaN(minValue) && value < Number(minValue)) &&
  !(!Number.isNaN(maxValue) && value > Number(maxValue));

export const validateValue = ({
  value,
  minValue,
  maxValue,
}: {
  value: InputValue;
  minValue: MinValue;
  maxValue: MaxValue;
}): {
  hasError: boolean;
  validationType?: 'outOfBoundsError' | 'formatError';
} => {
  if (isEmptyValue(value)) {
    return { hasError: false };
  }

  if (isInvalidNumber(value)) {
    return { hasError: true, validationType: 'formatError' };
  }

  if (
    !isInRange({
      value: Number(value),
      minValue,
      maxValue,
    })
  ) {
    return { hasError: true, validationType: 'outOfBoundsError' };
  }

  return { hasError: false };
};

export type DIRECTION = 'up' | 'down';

export const getClosestValue = (
  currentValue: number,
  options: Array<number>,
  direction: DIRECTION,
): number => {
  const sortedOptions = [...options];
  sortedOptions.sort((a, b) => (direction === 'up' ? a - b : b - a));
  return (
    sortedOptions.find(v =>
      direction === 'up' ? v > currentValue : v < currentValue,
    ) ?? sortedOptions[sortedOptions.length - 1]
  );
};
